import { Link } from 'react-router-dom';
import {
  Building2,
  Shield,
  Users,
  Headphones,
  FileCheck,
  Workflow,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { AnimatedSection } from '@/components/marketing/AnimatedSection';

const pillars = [
  {
    icon: Building2,
    title: 'Multi-location command',
    body: 'One operating system across branches. Shared playbooks, separate books. Owners see the whole company; managers stay inside their yard.',
  },
  {
    icon: Users,
    title: 'No seat cap, real roles',
    body: 'Every published plan is month-to-month, and Business has no user cap ($99 per active user). Owners, sales, office, field, and finance each get a workspace that matches the job.',
  },
  {
    icon: Shield,
    title: 'Buyer-ready security',
    body: 'Tenant isolation, encryption in transit and at rest, and a written security review on request. We do not publish infrastructure internals.',
  },
  {
    icon: Headphones,
    title: 'White-glove rollout',
    body: 'Business includes a dedicated account manager and white-glove onboarding, with a live walkthrough built around your storm and retail workflows.',
  },
];

const procurement = [
  'Month-to-month published pricing — Starter $0, Pro $79/user, Business $99/user',
  'Free during beta — founding members lock in 30% off when billing starts',
  'Invoice and ACH available on Business after the first billing cycle',
  'Security questionnaire and DPA on request — no fake certifications',
  'Sandbox workspace with fictional demo data for training, not live jobs',
  'Export-your-data commitment under the Data Freedom pledge',
];

const rollout = [
  {
    step: '01',
    title: 'Scope',
    detail: 'Map your current stack: leads, estimates, proposals, jobs, money, and marketing.',
  },
  {
    step: '02',
    title: 'Migrate',
    detail:
      'White-glove import of contacts and open jobs on Business. CSV self-serve on every plan.',
  },
  {
    step: '03',
    title: 'Train',
    detail: 'Office, sales, and field sessions. Help lives at the public contractor docs site.',
  },
  {
    step: '04',
    title: 'Run',
    detail: 'Storm calendar, canvassing routes, and invoice reminders go live in the first week.',
  },
];

/** Present enterprise procurement, security, and rollout information. */
export function EnterprisePage() {
  return (
    <>
      <SEOHead
        title="Enterprise Roofing OS — Multi-location Teams"
        description="Rooflyzer for multi-location roofing companies: tenant isolation, white-glove onboarding, and the published Business plan at $99/user/month."
        canonicalUrl="/enterprise"
      />

      <section className="relative pt-20 pb-16 px-4 sm:px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider mb-6">
              Enterprise
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight text-white mb-6">
              One OS for every yard,{' '}
              <span className="text-white">without a software committee.</span>
            </h1>
            <p className="text-lg text-white/60 max-w-2xl mb-8">
              Built for operators running more than one crew or location. Published pricing.
              Per-user pricing, with no user cap on Business. A security review you can actually
              send to procurement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm"
              >
                <Link to="/demo">
                  Book an enterprise walkthrough
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-lg border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60"
              >
                <Link to="/security">Read security posture</Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          {pillars.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className="rounded-2xl bg-slate-900/60 border border-slate-800 p-8"
              >
                <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center mb-5">
                  <Icon className="size-5 text-emerald-400" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-3">{item.title}</h2>
                <p className="text-white/60 leading-relaxed">{item.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl font-display font-bold text-white mb-4">
              What procurement actually asks
            </h2>
            <p className="text-white/55 mb-6">
              No hidden per-report fees. No surprise seat licenses. Business is the plan built for
              multi-location restorers and PE-backed operators.
            </p>
            <ul className="space-y-3">
              {procurement.map((line) => (
                <li key={line} className="flex items-start gap-3 text-white/75">
                  <CheckCircle2 className="size-5 text-emerald-400 shrink-0 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-8">
            <p className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">
              Published Business plan
            </p>
            <p className="text-5xl font-display font-bold text-white">
              $99<span className="text-xl text-white/45 font-medium">/user/mo</span>
            </p>
            <p className="text-sm text-white/50 mt-2 mb-6">
              Per-user pricing, month-to-month. White-glove onboarding included.
            </p>
            <ul className="space-y-2 text-sm text-white/70 mb-8">
              <li>AI lead scoring and custom workflows</li>
              <li>Job costing and commission tracking</li>
              <li>SmartRoof measurements included</li>
              <li>Dedicated account manager + white-glove onboarding</li>
            </ul>
            <Button
              asChild
              className="w-full rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm"
            >
              <Link to="/pricing">Compare all plans</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-white mb-10">
            Rollout in four weeks, not four quarters
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {rollout.map((item) => (
              <div
                key={item.step}
                className="rounded-2xl border border-slate-800 p-6 bg-slate-900/60"
              >
                <p className="text-emerald-400 font-mono font-semibold mb-3">{item.step}</p>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-white/55">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto rounded-3xl border border-slate-800 bg-slate-900/60 p-10 text-center">
          <Workflow className="size-8 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-3xl font-display font-bold text-white mb-3">
            Ready for a security review?
          </h2>
          <p className="text-white/55 mb-8">
            Call (370) 289-8830 or book a 30-minute walkthrough. We send the questionnaire packet,
            not a slide deck full of internals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm"
            >
              <Link to="/demo">Schedule the walkthrough</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-lg border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60"
            >
              <Link to="/data-freedom">
                Data Freedom pledge
                <FileCheck className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
